import axios, { AxiosInstance } from 'axios';
import {
  PartnerLoginRequest,
  PartnerRegisterRequest,
  PartnerLoginResponse,
  PartnerProfileResponse,
  PartnerRefreshTokenResponse,
  PartnerAccessToken,
  PartnerClubAssignment
} from '../types/partner';
import { getPartnerToken, getPartnerRefreshToken, setPartnerToken, setPartnerRefreshToken } from '../utils/partnerAuth';

// Resolve API base: use absolute VITE_API_BASE when provided; otherwise current origin.
const _configured = (import.meta as any).env?.VITE_API_BASE as string | undefined;
const API_BASE_URL = _configured && /^https?:\/\//i.test(_configured) ? _configured : window.location.origin;

/**
 * Create axios instance for partner API calls
 */
const createPartnerApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: `${API_BASE_URL}/api/partner`,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor to add auth token
  client.interceptors.request.use(
    (config) => {
      const token = getPartnerToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor to handle token refresh
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // If 401 and we haven't retried yet, try to refresh token
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const refreshToken = getPartnerRefreshToken();
          if (refreshToken) {
            const response = await axios.post<{ data: PartnerRefreshTokenResponse }>(
              `${API_BASE_URL}/api/partner/auth/refresh`,
              { refreshToken },
              { headers: { 'Content-Type': 'application/json' } }
            );

            const { token, refreshToken: newRefreshToken } = response.data.data;
            setPartnerToken(token);
            setPartnerRefreshToken(newRefreshToken);

            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return client(originalRequest);
          }
        } catch (refreshError) {
          // Refresh failed, redirect to login
          window.location.href = '/partner/login';
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
};

const partnerApiClient = createPartnerApiClient();

/**
 * Partner authentication API
 */
export const partnerAuthApi = {

  /**
   * Login with email and password
   */
  login: async (data: PartnerLoginRequest): Promise<PartnerLoginResponse> => {
    const response = await partnerApiClient.post<{ data: PartnerLoginResponse }>(
      '/auth/login',
      data
    );

    // Store refresh token
    if (response.data.data.refreshToken) {
      setPartnerRefreshToken(response.data.data.refreshToken);
    }

    return response.data.data;
  },

  /**
   * Register new partner user
   * Returns login response with JWT token (email auto-verified)
   */
  register: async (data: PartnerRegisterRequest): Promise<PartnerLoginResponse> => {
    const response = await partnerApiClient.post('/auth/register', data);
    return response.data.data;
  },

  /**
   * Verify email with token
   */
  verifyEmail: async (token: string): Promise<void> => {
    await partnerApiClient.post('/auth/verify-email', { token });
  },

  /**
   * Get current partner user profile
   */
  getProfile: async (): Promise<PartnerProfileResponse> => {
    const response = await partnerApiClient.get<{ data: PartnerProfileResponse }>('/auth/profile');
    return response.data.data;
  },

  /**
   * Refresh access token
   */
  refreshToken: async (): Promise<PartnerRefreshTokenResponse> => {
    const refreshToken = getPartnerRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await partnerApiClient.post<{ data: PartnerRefreshTokenResponse }>(
      '/auth/refresh',
      { refreshToken }
    );

    // Store new refresh token
    if (response.data.data.refreshToken) {
      setPartnerRefreshToken(response.data.data.refreshToken);
    }

    return response.data.data;
  },

  /**
   * Logout current session
   */
  logout: async (): Promise<void> => {
    const refreshToken = getPartnerRefreshToken();
    if (refreshToken) {
      await partnerApiClient.post('/auth/logout', { refreshToken });
    }
  },

  /**
   * Logout all sessions
   */
  logoutAll: async (): Promise<void> => {
    await partnerApiClient.post('/auth/logout-all');
  },
};

/**
 * Partner access API
 */
export const partnerAccessApi = {
  /**
   * Get all access tokens for current partner
   */
  getAccessTokens: async (): Promise<PartnerAccessToken[]> => {
    const response = await partnerApiClient.get<{ data: PartnerAccessToken[] }>('/access/tokens');
    return response.data.data;
  },

  /**
   * Get all club assignments for current partner
   */
  getClubAssignments: async (): Promise<PartnerClubAssignment[]> => {
    const response = await partnerApiClient.get<{ data: PartnerClubAssignment[] }>('/access/clubs');
    return response.data.data;
  },


};

/**
 * Partner club data API
 */
export const partnerClubApi = {
  /**
   * Get club data (requires club access)
   */
  getClubData: async (clubId: number): Promise<any> => {
    const response = await partnerApiClient.get(`/clubs/${clubId}`);
    return response.data.data;
  },

  /**
   * Get club matches (requires club access)
   */
  getClubMatches: async (clubId: number): Promise<any[]> => {
    const response = await partnerApiClient.get(`/clubs/${clubId}/matches`);
    return response.data.data;
  },

  /**
   * Update match data (requires update permission)
   */
  updateMatch: async (clubId: number, matchId: number, data: any): Promise<any> => {
    const response = await partnerApiClient.put(`/clubs/${clubId}/matches/${matchId}`, data);
    return response.data.data;
  },
};

export default partnerApiClient;

